import { axios } from '@ben/api/axios';
import Banner from '@ben/orion/banner';
import Button from '@ben/orion/deprecated-button';
import Input, { InputVariant } from '@ben/orion/input';
import SchemaForm from '@ben/orion/schema-form';
import Spinner from '@ben/orion/spinner';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { RJSFSchema } from '@rjsf/utils';
import { fromPairs, mapValues } from 'lodash';
import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { CenteredContainer } from '../../components/shared/centered-container';

const AutoBenScreen = () => {
  const [requirements, setRequirements] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState<unknown>();
  const navigate = useNavigate();
  const formId = useId();
  const [generating, setGenerating] = useState(false);
  const messages = [
    'Generating benefit...',
    'Please wait...',
    'Almost there...',
  ];
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(
      () => {
        setCurrentMessageIndex(
          (previousIndex) => (previousIndex + 1) % messages.length
        );
      },
      Math.random() * (4000 - 2000) + 2000
    );

    return () => clearInterval(interval);
  }, [messages.length]);

  const submitPartialRequirements = async () => {
    setLoading(true);

    const response = await axios.post(
      'refine/',
      {
        requirements: requirements,
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );
    const transformedSchema = convertTuplesToObject(
      response.data as Record<string, unknown>
    );
    setSchema(transformedSchema);
    setLoading(false);
  };

  const submitRequirements = async (v: Record<string, unknown>) => {
    setGenerating(true);

    const response = await axios.post(
      'autoben/',
      {
        requirements: requirements + '\n' + JSON.stringify(v),
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );
    setGenerating(false);
    navigate('/benefits/create', {
      state: {
        initialBenefit: response.data,
      },
    });
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <Typography variant={TypographyVariant.h1}>AutoBen</Typography>
      </HeaderWrapper>
      {generating ? (
        <CenteredContainer>
          <Spinner />
          <Typography variant={TypographyVariant.bodyMd1}>
            {messages[currentMessageIndex]}
          </Typography>
        </CenteredContainer>
      ) : (
        <>
          <Typography variant={TypographyVariant.bodyMd1}>
            AutoBen is a tool to help kickstart the benefit configuration from a
            set of requirements.
          </Typography>
          <StickyBanner>
            <Banner
              variant="information"
              message="Do not use sensitive or personal information such as emails in the prompt."
            />
          </StickyBanner>
          <br />
          <Input
            name="Requirements"
            showLabel
            value={requirements}
            onChange={(event) => setRequirements(event.target.value)}
            variant={InputVariant.TextArea}
            placeholder="Enter requirements here"
          />
          <br />
          {schema ? (
            <SchemaForm
              id={formId}
              schema={schema}
              onSubmit={(v) => {
                void submitRequirements(v).catch(console.error);
              }}
            />
          ) : (
            <Button
              label={'Submit'}
              onClick={() => {
                void submitPartialRequirements().catch(console.error);
              }}
              isLoading={loading}
            />
          )}
        </>
      )}
    </Wrapper>
  );
};

export default AutoBenScreen;

const convertTuplesToObject = (data: unknown): unknown => {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    const isTupleArray = data.every(
      (item) =>
        Array.isArray(item) && item.length === 2 && typeof item[0] === 'string'
    );

    if (isTupleArray) {
      const filtered = data.filter(([, value]) => value !== null);
      const converted = fromPairs(filtered);
      const recursivelyConverted = mapValues(converted, (value) =>
        convertTuplesToObject(value)
      );
      return Object.fromEntries(
        Object.entries(recursivelyConverted).filter(
          ([, value]) => value !== null
        )
      );
    }

    return data.map((item) => convertTuplesToObject(item));
  }

  if (typeof data === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      const convertedValue = convertTuplesToObject(value);
      if (convertedValue !== null) {
        result[key] = convertedValue;
      }
    }
    return result;
  }

  return data;
};

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(5)};
  `}
`;

const HeaderWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${theme.spacing(3)};
  `}
`;

const StickyBanner = styled.div`
  ${({ theme }) => css`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${theme.palette.offWhite};
  `}
`;
